import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './connectors.css';
import '../../scans/scans.css';
import '../../assets/assets.css';
import '../../vulnerabilities/vulnerabilities.css';
import { queryClient } from '../../../queryClient';
import {
  connectorsPageQueryKey,
  connectorsSitesQueryKey,
} from '../../../queryCacheScope';
import { listZapConnectors, listOpenvasConnectors } from '../../../services/connector.service';
import { getSites } from '../../../services/asset.service';
import { toUserFacingErrorMessage } from '../../../services/_core';
import {
  compareDates,
  compareLocaleStrings,
  stableSortTableRows,
  toggleTableSortConfig,
} from '../../../utils/tableSort';
import { useTableColumnResize } from '../../../utils/tableColumnResize';
import {
  CONNECTORS_TABLE_COLUMNS,
  CONNECTORS_TABLE_COLUMN_WIDTHS_KEY,
} from '../../../utils/resizableTableColumns';
import { ResizableColgroup, ResizableTableHead } from '../../../components/ResizableTableHead';
import BlueXLoader, { PageDataLoader } from '../../../components/BlueXLoader';
import { TrashIcon } from '../../../shared/components/TrashIcon';
import TablePagination, { TABLE_PAGE_SIZE, usePaginatedRows } from '../../../shared/components/TablePagination';
import {
  displayConnectorName,
  filterConnectorsByName,
  formatConnectorLocation,
  formatConnectorOrgSites,
  groupConnectorOrgSites,
  listConnectorOrgSites,
  siteLocationLabels,
  getOpenvasConnectorInitialState,
  getZapConnectorInitialState,
  isPermissionDeniedError,
  mapAssignedSitesFromConnector,
  sanitizeConnectorSearchInput,
  sanitizeOpenvasConnectorField,
  sanitizeZapConnectorField,
} from './connectorFormUtils';

export {
  getOpenvasConnectorInitialState,
  getZapConnectorInitialState,
  isPermissionDeniedError,
  sanitizeOpenvasConnectorField,
  sanitizeZapConnectorField,
};

/** Match Assets / Scans table sort affordance (⇅ / ↑ / ↓). */
function getScansStyleSortIcon(sortConfig, key) {
  if (sortConfig?.key !== key) return '⇅';
  return sortConfig.direction === 'asc' ? '↑' : '↓';
}

function ConnectorOrgSiteCell({ groups, fallback }) {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const orgGroups = Array.isArray(groups) ? groups.filter((group) => group && (group.org || (group.sites || []).length)) : [];

  const popoverItems = useMemo(() => {
    const items = [];
    const seen = new Set();
    orgGroups.forEach((group) => {
      const orgLabel = String(group?.org || '').trim();
      const sites = Array.isArray(group?.sites) ? group.sites.filter(Boolean) : [];
      if (sites.length === 0) {
        if (orgLabel && !seen.has(orgLabel)) {
          seen.add(orgLabel);
          items.push({ key: orgLabel, org: orgLabel, site: '' });
        }
        return;
      }
      sites.forEach((site) => {
        if (seen.has(site)) return;
        seen.add(site);
        items.push({
          key: site,
          org: orgLabel,
          site,
        });
      });
    });
    return items;
  }, [orgGroups]);

  const showHoverPopover = popoverItems.length > 1;

  const placePopover = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.max(220, Math.min(320, rect.width + 40));
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const estimatedHeight = Math.min(280, 16 + popoverItems.length * 36);
    const openAbove = rect.bottom + 8 + estimatedHeight > window.innerHeight && rect.top > estimatedHeight;
    setPosition({
      left,
      top: openAbove ? rect.top - 8 : rect.bottom + 8,
      width,
      above: openAbove,
    });
  }, [popoverItems.length]);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openPopover = useCallback(() => {
    if (!showHoverPopover) return;
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer, showHoverPopover]);

  const scheduleClosePopover = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  }, [clearCloseTimer]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    placePopover();
    const close = () => setOpen(false);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('resize', placePopover);
    window.addEventListener('scroll', close, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', placePopover);
      window.removeEventListener('scroll', close, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, placePopover]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  if (orgGroups.length === 0) return fallback || '—';

  const primary = orgGroups[0];
  const primaryOrg = String(primary?.org || '').trim();
  const primarySite = Array.isArray(primary?.sites) && primary.sites.length ? String(primary.sites[0] || '').trim() : '';
  const extraSiteCount = Math.max(0, popoverItems.length - 1);

  let orgLabel = primaryOrg;
  let siteLabel = '';
  if (primaryOrg) {
    siteLabel = primarySite;
  } else if (primarySite) {
    orgLabel = primarySite;
  } else {
    orgLabel = fallback && fallback !== '—' ? fallback : 'Assigned sites';
  }

  return (
    <span
      ref={triggerRef}
      className={`connectors-org-site-summary${showHoverPopover ? ' connectors-org-site-summary--interactive' : ''}`}
      onMouseEnter={openPopover}
      onMouseLeave={scheduleClosePopover}
      onFocus={openPopover}
      onBlur={scheduleClosePopover}
      tabIndex={showHoverPopover ? 0 : undefined}
      aria-describedby={open ? 'connectors-org-site-popover' : undefined}
    >
      <span className="connectors-org-site-stack">
        <span className="connectors-org-site-org" title={orgLabel}>{orgLabel}</span>
        {siteLabel || extraSiteCount > 0 ? (
          <span className="connectors-org-site-site" title={siteLabel || `${extraSiteCount} more sites`}>
            {siteLabel ? (
              <>
                {siteLabel}
                {extraSiteCount > 0 ? (
                  <span className="connectors-org-site-more-inline">{` +${extraSiteCount} more`}</span>
                ) : null}
              </>
            ) : (
              <span className="connectors-org-site-more-inline">{`+${extraSiteCount} more`}</span>
            )}
          </span>
        ) : null}
      </span>
      {open && position && showHoverPopover
        ? createPortal(
          <div
            id="connectors-org-site-popover"
            ref={popoverRef}
            className={`connectors-org-site-popover${position.above ? ' connectors-org-site-popover--above' : ''}${
              document.querySelector('.dashboard-container.theme-light') ? ' connectors-org-site-popover--light' : ''
            }`}
            style={{ top: position.top, left: position.left, width: position.width }}
            role="list"
            onMouseEnter={openPopover}
            onMouseLeave={scheduleClosePopover}
          >
            {popoverItems.map((item) => (
              <div key={item.key} className="connectors-org-site-popover-item" role="listitem">
                {item.site || item.org}
              </div>
            ))}
          </div>,
          document.body
        )
        : null}
    </span>
  );
}

const CONNECTORS_CACHE_MS = 3 * 60 * 1000;
const connectorsCacheKey = (tab) => connectorsPageQueryKey(tab);
const connectorsSitesCacheKey = () => connectorsSitesQueryKey();

function readConnectorsCache(tab) {
  return queryClient.getQueryData(connectorsCacheKey(tab)) || null;
}

function connectorsCacheIsFresh(tab) {
  const updatedAt = queryClient.getQueryState(connectorsCacheKey(tab))?.dataUpdatedAt || 0;
  return updatedAt > 0 && Date.now() - updatedAt < CONNECTORS_CACHE_MS;
}

export function cloneZapAssignmentRows() {
  return [];
}

export const CONNECTORS_SETTINGS_HINT =
  'Integrations that send findings, tickets, or alerts to external systems. Wire this list to your admin API when connectors are available.';

function formatConnectorTableDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 16);
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

function isExplicitFalse(value) {
  if (value === false || value === 0) return true;
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'false' || normalized === '0' || normalized === 'off';
}

function isExplicitTrue(value) {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'on';
}

/** Resolve connector status based on enablement and live healthcheck/test results. */
export function resolveConnectorRowStatus({ enabled, scanner_status, status }) {
  if (isExplicitFalse(enabled)) return 'Disconnected';
  if (isExplicitFalse(scanner_status)) return 'Error';
  if (isExplicitTrue(scanner_status)) return 'Connected';
  if (typeof status === 'string' && status.trim()) {
    const s = status.trim().toLowerCase();
    if (s === 'connected' || s === 'online') return 'Connected';
    if (s === 'disconnected' || s === 'offline') return 'Disconnected';
    if (s === 'error' || s === 'failed') return 'Error';
    return status;
  }
  if (isExplicitTrue(enabled)) return 'Connected';
  return 'Unknown';
}

function connectorStatusClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'connected' || (s.includes('connect') && !s.includes('disconnect'))) return 'connected';
  if (s.includes('error') || s.includes('fail')) return 'error';
  if (s.includes('disconnect')) return 'disconnected';
  return 'neutral';
}

/** Settings — Connectors: same layout language as subscription usage + assets tables. */
function ConnectorsSettingsPage({
  searchQuery: _searchQuery,
  onSearchQueryChange,
  onConfigureConnector,
  onDeleteConnector,
  onTestConnector,
  refreshKey,
  canManageConnectors = false,
  scannerTab,
  onScannerTabChange,
}) {
  const searchId = useId();
  const [internalScannerTab, setInternalScannerTab] = useState('web');
  const connectorsScannerTab = scannerTab === 'network' || scannerTab === 'web' ? scannerTab : internalScannerTab;
  const setConnectorsScannerTab = onScannerTabChange || setInternalScannerTab;
  const [webScannerRows, setWebScannerRows] = useState(
    () => readConnectorsCache('web')?.rows || []
  );
  const [networkScannerRows, setNetworkScannerRows] = useState(
    () => readConnectorsCache('network')?.rows || []
  );
  const [connectorsLoading, setConnectorsLoading] = useState(() => {
    const initialTab = scannerTab === 'network' || scannerTab === 'web' ? scannerTab : 'web';
    return readConnectorsCache(initialTab) == null;
  });
  const [connectorsLoadError, setConnectorsLoadError] = useState('');
  const [testingById, setTestingById] = useState({});
  const [testResultById, setTestResultById] = useState({});
  const [actionFeedback, setActionFeedback] = useState(null);
  const [tableSearch, setTableSearch] = useState('');
  const [openConnectorRowMenuKey, setOpenConnectorRowMenuKey] = useState(null);
  const [connectorRowMenuPlacement, setConnectorRowMenuPlacement] = useState(null);
  const connectorRowActionsMenuRef = useRef(null);
  const connectorRowActionsTriggerRef = useRef(null);
  const lastRefreshKeyRef = useRef(refreshKey);

  const activeConnectorRows = connectorsScannerTab === 'web' ? webScannerRows : networkScannerRows;

  const mapWebScannerRows = useCallback((rows) => {
    return rows.map((row, index) => {
      const settingId = String(row?.setting_id || row?.id || `web-${index}`).trim();
      const vanityName = String(row?.scanner_vanity_name || '').trim();
      const technicalName = String(row?.scanner_name || '').trim();
      const assignments = mapAssignedSitesFromConnector(row);
      const display = displayConnectorName({
        vanityName,
        technicalName,
        name: vanityName || technicalName,
      });
      return {
        id: settingId,
        name: display.primary,
        vanityName,
        technicalName,
        assignmentSummary: formatConnectorLocation(assignments),
        orgSite: formatConnectorOrgSites(assignments),
        orgSites: listConnectorOrgSites(assignments),
        orgSiteGroups: groupConnectorOrgSites(assignments),
        assignments,
        status: resolveConnectorRowStatus({
          enabled: row?.enabled,
          scanner_status: row?.scanner_status,
          status: row?.status,
        }),
        scannerStatus: row?.scanner_status,
        lastUpdated: row?.updated_time || row?.created_time || row?.updated_at || row?.last_updated || '',
      };
    });
  }, []);

  const mapNetworkScannerRows = useCallback((rows) => {
    return rows.map((row, index) => {
      const settingId = String(row?.setting_id || row?.id || `network-${index}`).trim();
      const vanityName = String(row?.openvas_vanity_name || row?.scanner_vanity_name || '').trim();
      const technicalName = String(row?.openvas_name || row?.scanner_name || '').trim();
      const assignments = mapAssignedSitesFromConnector(row);
      const display = displayConnectorName({
        vanityName,
        technicalName,
        name: vanityName || technicalName,
      });
      return {
        id: settingId,
        name: display.primary,
        vanityName,
        technicalName,
        assignmentSummary: formatConnectorLocation(assignments, {
          perimeterScanner: row?.perimeter_scanner === true,
        }),
        orgSite: formatConnectorOrgSites(assignments),
        orgSites: listConnectorOrgSites(assignments),
        orgSiteGroups: groupConnectorOrgSites(assignments),
        assignments,
        status: resolveConnectorRowStatus({
          enabled: row?.openvas_enabled ?? row?.scanner_enabled,
          scanner_status: row?.scanner_status,
          status: row?.status,
        }),
        scannerStatus: row?.scanner_status,
        lastUpdated: row?.created_time || row?.updated_at || row?.last_updated || '',
      };
    });
  }, []);

  const dedupeByConnectorId = useCallback((rows) => {
    const byId = new Map();
    rows.forEach((row) => {
      const key = String(row?.id || '').trim();
      if (!key) return;
      if (!byId.has(key)) byId.set(key, row);
    });
    return Array.from(byId.values());
  }, []);

  const loadConnectorRows = useCallback(async ({ tab = connectorsScannerTab, fresh = false } = {}) => {
    const targetTab = tab === 'network' ? 'network' : 'web';
    const cacheKey = connectorsCacheKey(targetTab);
    const cached = readConnectorsCache(targetTab);

    if (cached && !fresh) {
      if (targetTab === 'web') setWebScannerRows(cached.rows || []);
      else setNetworkScannerRows(cached.rows || []);
      setConnectorsLoading(false);
      if (connectorsCacheIsFresh(targetTab)) return;
    } else if (!cached) {
      setConnectorsLoading(true);
    }

    if (fresh) {
      const otherTab = targetTab === 'web' ? 'network' : 'web';
      queryClient.removeQueries({ queryKey: connectorsCacheKey(otherTab) });
      queryClient.removeQueries({ queryKey: connectorsSitesCacheKey() });
    }

    setConnectorsLoadError('');

    try {
      const cachedSites = queryClient.getQueryData(connectorsSitesCacheKey());
      const sitesFresh =
        (queryClient.getQueryState(connectorsSitesCacheKey())?.dataUpdatedAt || 0) > 0 &&
        Date.now() - (queryClient.getQueryState(connectorsSitesCacheKey())?.dataUpdatedAt || 0) < CONNECTORS_CACHE_MS;

      const listPromise =
        targetTab === 'web'
          ? listZapConnectors().catch(() => null)
          : listOpenvasConnectors().catch(() => null);
      const sitesPromise =
        cachedSites && sitesFresh && !fresh
          ? Promise.resolve(cachedSites)
          : getSites()
              .then((rows) => {
                const next = Array.isArray(rows) ? rows : [];
                queryClient.setQueryData(connectorsSitesCacheKey(), next);
                return next;
              })
              .catch(() => (Array.isArray(cachedSites) ? cachedSites : []));

      const [rowsRaw, siteRowsRaw] = await Promise.all([listPromise, sitesPromise]);

      const locationById = new Map();
      (Array.isArray(siteRowsRaw) ? siteRowsRaw : []).forEach((site) => {
        const id = String(site?.uu_id || site?.site_uuid || site?.id || '').trim();
        if (!id) return;
        locationById.set(id, {
          locations: siteLocationLabels(site),
          organization: typeof site?.organization === 'string'
            ? site.organization
            : (site?.organization_name || site?.org_name || ''),
          siteName: site?.site_name || site?.name || '',
        });
      });
      const withLocations = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({
        ...row,
        assigned_sites: (Array.isArray(row?.assigned_sites) ? row.assigned_sites : []).map((site) => {
          const id = String(site?.uu_id || site?.id || '').trim();
          const locations = Array.isArray(site?.locations) && site.locations.length
            ? site.locations
            : (locationById.get(id)?.locations || []);
          const full = locationById.get(id);
          return {
            ...site,
            locations,
            organization: full?.organization || site?.organization || '',
            site_name: full?.siteName || site?.site_name || site?.name,
          };
        }),
      }));

      const mappedRows = dedupeByConnectorId(
        targetTab === 'web'
          ? mapWebScannerRows(withLocations(rowsRaw))
          : mapNetworkScannerRows(withLocations(rowsRaw))
      );

      const nextRows = mappedRows;

      if (targetTab === 'web') setWebScannerRows(nextRows);
      else setNetworkScannerRows(nextRows);

      if (rowsRaw !== null) {
        queryClient.setQueryData(cacheKey, { rows: nextRows });
      } else {
        setConnectorsLoadError('Could not load connector list. Please try again.');
      }
    } catch (err) {
      setConnectorsLoadError(
        toUserFacingErrorMessage(err?.message, 'Failed to load connectors. Please try again.')
      );
    } finally {
      setConnectorsLoading(false);
    }
  }, [connectorsScannerTab, dedupeByConnectorId, mapNetworkScannerRows, mapWebScannerRows]);

  useEffect(() => {
    const fresh = lastRefreshKeyRef.current !== refreshKey;
    lastRefreshKeyRef.current = refreshKey;
    loadConnectorRows({ tab: connectorsScannerTab, fresh });
    // Prefetch the other tab so counts are accurate (Web vs Network are separate APIs).
    const otherTab = connectorsScannerTab === 'web' ? 'network' : 'web';
    loadConnectorRows({ tab: otherTab, fresh: false });
  }, [loadConnectorRows, refreshKey, connectorsScannerTab]);

  useEffect(() => {
    const onTab = (event) => {
      const tab = String(event?.detail?.tab || '').trim();
      if (tab === 'web' || tab === 'network') setConnectorsScannerTab(tab);
    };
    const onOpenConfig = () => {
      const configure = document.querySelector('[data-tour="settings-connectors-configure"]');
      if (configure) configure.click();
    };
    window.addEventListener('xspectre:tour-connectors-tab', onTab);
    window.addEventListener('xspectre:tour-open-connector-config', onOpenConfig);
    return () => {
      window.removeEventListener('xspectre:tour-connectors-tab', onTab);
      window.removeEventListener('xspectre:tour-open-connector-config', onOpenConfig);
    };
  }, [setConnectorsScannerTab]);

  const filteredRows = useMemo(
    () => filterConnectorsByName(activeConnectorRows, tableSearch),
    [tableSearch, activeConnectorRows]
  );

  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const handleConnectorSort = useCallback((key) => {
    setSortConfig((prev) => toggleTableSortConfig(prev, key));
  }, []);

  const getConnectorSortIcon = useCallback((key) => getScansStyleSortIcon(sortConfig, key), [sortConfig]);

  const {
    widths: connectorColumnWidths,
    onResizeStart: onConnectorColumnResizeStart,
    tableMinWidth: connectorsTableMinWidth,
    resizingKey: connectorColumnResizingKey,
  } = useTableColumnResize(CONNECTORS_TABLE_COLUMNS, CONNECTORS_TABLE_COLUMN_WIDTHS_KEY);

  const sortedRows = useMemo(
    () =>
      stableSortTableRows(filteredRows, sortConfig, (a, b, key) => {
        if (key === 'name') return compareLocaleStrings(a.name, b.name);
        if (key === 'assignment' || key === 'assignmentSummary') {
          return compareLocaleStrings(a.assignmentSummary, b.assignmentSummary);
        }
        if (key === 'orgSite') return compareLocaleStrings(a.orgSite, b.orgSite);
        if (key === 'status') return compareLocaleStrings(a.status, b.status);
        if (key === 'lastUpdated') return compareDates(a.lastUpdated, b.lastUpdated);
        return 0;
      }),
    [filteredRows, sortConfig]
  );
  const tablePagination = usePaginatedRows(sortedRows, TABLE_PAGE_SIZE);
  const visibleRows = tablePagination.pageRows;

  const closeConnectorRowActionsMenu = useCallback(() => {
    setOpenConnectorRowMenuKey(null);
    setConnectorRowMenuPlacement(null);
  }, []);

  useEffect(() => {
    closeConnectorRowActionsMenu();
  }, [connectorsScannerTab, closeConnectorRowActionsMenu]);

  useEffect(() => {
    if (!openConnectorRowMenuKey) return undefined;
    const onPointerDown = (event) => {
      const inMenu = connectorRowActionsMenuRef.current?.contains(event.target);
      const inTrigger = connectorRowActionsTriggerRef.current?.contains(event.target);
      if (!inMenu && !inTrigger) closeConnectorRowActionsMenu();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeConnectorRowActionsMenu();
    };
    const onRepositionClose = () => closeConnectorRowActionsMenu();
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onRepositionClose);
    window.addEventListener('scroll', onRepositionClose, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onRepositionClose);
      window.removeEventListener('scroll', onRepositionClose, true);
    };
  }, [openConnectorRowMenuKey, closeConnectorRowActionsMenu]);

  const runConnectorTest = useCallback(
    async (row) => {
      if (!onTestConnector || !row?.id || testingById[row.id]) return;
      setTestingById((prev) => ({ ...prev, [row.id]: true }));
      setTestResultById((prev) => ({ ...prev, [row.id]: '' }));
      setActionFeedback(null);
      try {
        const result = await onTestConnector(row.id, connectorsScannerTab);
        const ok = !!(result && (result.ok === true || result.status === 'ok'));
        const detailMsg =
          result?.detail ||
          (ok ? 'Connection successful.' : 'Connection test failed.');
        setTestResultById((prev) => ({
          ...prev,
          [row.id]: detailMsg,
        }));
        setActionFeedback({
          type: ok ? 'success' : 'error',
          message: `${row.name}: ${detailMsg}`,
        });
        const updateRows = (prevRows) =>
          prevRows.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  status: resolveConnectorRowStatus({
                    enabled: true,
                    scanner_status: ok,
                  }),
                  lastUpdated: result?.tested_at || new Date().toISOString(),
                }
              : r
          );
        if (connectorsScannerTab === 'web') {
          setWebScannerRows(updateRows);
        } else {
          setNetworkScannerRows(updateRows);
        }
      } catch (err) {
        const errMsg = toUserFacingErrorMessage(
          err?.message,
          'Connection test failed.'
        );
        setTestResultById((prev) => ({
          ...prev,
          [row.id]: errMsg,
        }));
        setActionFeedback({
          type: 'error',
          message: `${row.name}: ${errMsg}`,
        });
      } finally {
        setTestingById((prev) => ({ ...prev, [row.id]: false }));
      }
    },
    [connectorsScannerTab, onTestConnector, testingById]
  );

  const tableHeaderRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [headerGutterPx, setHeaderGutterPx] = useState(8);

  const handleTableScroll = useCallback((event) => {
    if (tableHeaderRef.current) {
      tableHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  }, []);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const CONTENT_INSET_PX = 16;

    const syncGutter = () => {
      const scrollbarLane = Math.max(0, Math.round(el.offsetWidth - el.clientWidth));
      const next = scrollbarLane + CONTENT_INSET_PX;
      setHeaderGutterPx((prev) => (prev === next ? prev : next));
    };

    syncGutter();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(syncGutter) : null;
    ro?.observe(el);
    window.addEventListener('resize', syncGutter);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', syncGutter);
    };
  }, [connectorsLoading, sortedRows.length]);

  const connectorsTableClassName =
    'assets-table assets-inventory-table assets-inventory-table--resizable risks-page-table connectors-settings-table';
  const connectorsTableStyle = { width: '100%', minWidth: connectorsTableMinWidth };

  const searchPlaceholder =
    connectorsScannerTab === 'web' ? 'Search web scanners' : 'Search network scanners';

  const emptyMessage = connectorsLoadError
    ? 'Unable to load scanners.'
    : tableSearch.trim()
      ? `No ${connectorsScannerTab === 'web' ? 'web' : 'network'} scanners match your search.`
      : connectorsScannerTab === 'web'
        ? 'No web scanners assigned to your sites yet.'
        : 'No network scanners assigned to your sites yet.';

  const showInitialConnectorsLoader = connectorsLoading && activeConnectorRows.length === 0;

  if (showInitialConnectorsLoader) {
    return (
      <section className="assets-section connectors-settings-page" data-tour="settings-connectors-page">
        <PageDataLoader label="Loading connectors" />
      </section>
    );
  }

  return (
    <section className="assets-section connectors-settings-page">
      <div className="connectors-settings-table-stack">
        <div
          className="scans-network-scope-tabs connectors-settings-scanner-tabs"
          data-tour="settings-connectors-tabs"
          role="tablist"
          aria-label="Connector scanner type"
        >
          <div className="scans-network-scope-tabs-track connectors-settings-scanner-tabs-track">
            <button
              type="button"
              role="tab"
              id="connectors-tab-web-scanner"
              aria-selected={connectorsScannerTab === 'web'}
              aria-controls="connectors-panel-scanner"
              className={`scans-network-scope-tab${connectorsScannerTab === 'web' ? ' is-active' : ''}`}
              data-tour="settings-connectors-tab-web"
              onClick={() => {
                setConnectorsScannerTab('web');
                setTableSearch('');
              }}
            >
              <span className="scans-network-scope-tab-main">
                <span className="scans-network-scope-tab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M3.75 12h16.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M12 3.75c2.4 2.35 3.6 5.15 3.6 8.25s-1.2 5.9-3.6 8.25c-2.4-2.35-3.6-5.15-3.6-8.25s1.2-5.9 3.6-8.25Z" stroke="currentColor" strokeWidth="1.8" />
                  </svg>
                </span>
                <span className="scans-network-scope-tab-label">Web Scanner</span>
              </span>
              <span className="scans-network-scope-tab-count">{webScannerRows.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              id="connectors-tab-network-scanner"
              aria-selected={connectorsScannerTab === 'network'}
              aria-controls="connectors-panel-scanner"
              className={`scans-network-scope-tab${connectorsScannerTab === 'network' ? ' is-active' : ''}`}
              data-tour="settings-connectors-tab-network"
              onClick={() => {
                setConnectorsScannerTab('network');
                setTableSearch('');
              }}
            >
              <span className="scans-network-scope-tab-main">
                <span className="scans-network-scope-tab-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <rect x="3.5" y="3.5" width="6.2" height="6.2" rx="1.4" stroke="currentColor" strokeWidth="1.8" />
                    <rect x="14.3" y="3.5" width="6.2" height="6.2" rx="1.4" stroke="currentColor" strokeWidth="1.8" />
                    <rect x="8.9" y="14.3" width="6.2" height="6.2" rx="1.4" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M6.6 9.7v2.2c0 .8.6 1.4 1.4 1.4h1.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M17.4 9.7v2.2c0 .8-.6 1.4-1.4 1.4h-1.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="scans-network-scope-tab-label">Network Scanner</span>
              </span>
              <span className="scans-network-scope-tab-count">{networkScannerRows.length}</span>
            </button>
          </div>
        </div>
        <div
          className="scans-network-split-table-stack connectors-settings-search-table-block"
          id="connectors-panel-scanner"
          role="tabpanel"
          aria-labelledby={
            connectorsScannerTab === 'web'
              ? 'connectors-tab-web-scanner'
              : 'connectors-tab-network-scanner'
          }
        >
          <div className="assets-table-toolbar">
            <div className="subscription-org-management-search" data-tour="settings-connectors-search">
              <label htmlFor={searchId} className="edi-sr-only">
                {searchPlaceholder}
              </label>
              <div className="subscription-org-management-search-inner">
                <input
                  id={searchId}
                  type="search"
                  className="subscription-org-management-search-input"
                  placeholder={searchPlaceholder}
                  value={tableSearch}
                  onChange={(e) => {
                    const next = sanitizeConnectorSearchInput(e.target.value);
                    setTableSearch(next);
                    if (typeof onSearchQueryChange === 'function') onSearchQueryChange(next);
                  }}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={500}
                />
                {tableSearch ? (
                  <button
                    type="button"
                    className="ghost-exposure-sys-table-search-clear"
                    onClick={() => {
                      setTableSearch('');
                      if (typeof onSearchQueryChange === 'function') onSearchQueryChange('');
                    }}
                    aria-label="Clear connector search"
                  >
                    &times;
                  </button>
                ) : null}
              </div>
            </div>
          </div>
          {connectorsLoadError ? (
            <div className="profile-settings-message profile-settings-error" role="alert" style={{ marginBottom: 10 }}>
              <span>{connectorsLoadError}</span>
              {' '}
              <button
                type="button"
                className="ghost-exposure-sys-btn"
                style={{ marginLeft: 8 }}
                onClick={() => loadConnectorRows({ tab: connectorsScannerTab, fresh: true })}
              >
                Retry
              </button>
            </div>
          ) : null}
          {actionFeedback ? (
            <div
              className={`profile-settings-message profile-settings-${
                actionFeedback.type === 'success' ? 'success' : 'error'
              }`}
              role={actionFeedback.type === 'success' ? 'status' : 'alert'}
              style={{ marginBottom: 10 }}
            >
              {actionFeedback.message}
            </div>
          ) : null}
          {!canManageConnectors ? (
            <div className="profile-settings-message profile-settings-info" role="status" style={{ marginBottom: 10 }}>
              You have read-only access to connectors. Create, update, and delete actions are disabled for your role.
            </div>
          ) : null}
          <div className="assets-table-shell assets-table-shell--inventory-body" data-tour="settings-connectors-table">
            {connectorsLoading ? (
              <div className="risks-page-table-loading">
                <BlueXLoader />
              </div>
            ) : (
              <>
                <div
                  className="risks-page-table-header-frame"
                  style={{ '--risks-header-gutter': `${headerGutterPx}px` }}
                >
                  <div ref={tableHeaderRef} className="risks-page-table-header">
                    <table className={`${connectorsTableClassName} risks-page-table--header`} style={connectorsTableStyle}>
                      <ResizableColgroup columns={CONNECTORS_TABLE_COLUMNS} widths={connectorColumnWidths} />
                      <ResizableTableHead
                        columns={CONNECTORS_TABLE_COLUMNS}
                        widths={connectorColumnWidths}
                        onResizeStart={onConnectorColumnResizeStart}
                        resizingKey={connectorColumnResizingKey}
                        sortConfig={sortConfig}
                        onSort={handleConnectorSort}
                        getSortIcon={getConnectorSortIcon}
                        resizeHandleClassName="risks-page-col-resize-handle"
                      />
                    </table>
                  </div>
                  <div className="risks-page-table-header-gutter" aria-hidden="true" />
                </div>
                <div
                  ref={tableScrollRef}
                  className="assets-inventory-table-scroll assets-inventory-table-scroll--body risks-page-table-scroll"
                  onScroll={handleTableScroll}
                >
                  <table className={`${connectorsTableClassName} risks-page-table--body`} style={connectorsTableStyle}>
                    <ResizableColgroup columns={CONNECTORS_TABLE_COLUMNS} widths={connectorColumnWidths} />
                    <tbody>
                      {sortedRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="assets-empty-cell">
                            {emptyMessage}
                          </td>
                        </tr>
                      ) : (
                        visibleRows.map((row) => (
                          <tr key={row.id}>
                            <td className="assets-cell-ellipsis" title={row.technicalName || row.name}>
                              <span className="connectors-settings-name">{row.name}</span>
                              {row.technicalName && row.technicalName !== row.name ? (
                                <span className="connectors-settings-name-secondary">{row.technicalName}</span>
                              ) : null}
                            </td>
                            <td className="assets-cell-ellipsis" title={row.assignmentSummary}>
                              {row.assignmentSummary}
                            </td>
                            <td className="assets-cell-ellipsis connectors-settings-org-site" title={row.orgSites?.join(', ') || row.orgSite}>
                              <ConnectorOrgSiteCell groups={row.orgSiteGroups} fallback={row.orgSite} />
                            </td>
                            <td>
                              <span
                                className={`connectors-settings-status connectors-settings-status--${connectorStatusClass(
                                  row.status
                                )}`}
                              >
                                {row.status}
                              </span>
                              {testingById[row.id] ? (
                                <div
                                  style={{
                                    fontSize: 11,
                                    opacity: 0.8,
                                    marginTop: 4,
                                    color: 'var(--popup-muted-text)',
                                  }}
                                  aria-live="polite"
                                >
                                  Testing connection...
                                </div>
                              ) : testResultById[row.id] ? (
                                <div
                                  style={{
                                    fontSize: 11,
                                    opacity: 0.9,
                                    marginTop: 4,
                                    color: row.status === 'Connected' ? '#86efac' : '#fca5a5',
                                  }}
                                  aria-live="polite"
                                >
                                  {testResultById[row.id]}
                                </div>
                              ) : null}
                            </td>
                            <td>{formatConnectorTableDate(row.lastUpdated)}</td>
                            <td className="connectors-settings-td-actions">
                              <div className="connectors-settings-actions-cell">
                                {canManageConnectors ? (
                                  <>
                                    <button
                                      type="button"
                                      className="connectors-settings-row-action connectors-settings-row-action--danger connectors-settings-row-action--icon-only"
                                      title={`Delete ${row.name} connector`}
                                      aria-label={`Delete ${row.name} connector`}
                                      onClick={() => {
                                        closeConnectorRowActionsMenu();
                                        onDeleteConnector && onDeleteConnector(row.id, row.name, connectorsScannerTab);
                                      }}
                                    >
                                      <TrashIcon className="connectors-settings-delete-icon" size={16} strokeWidth={2.1} />
                                    </button>
                                    <div
                                      className="assets-row-actions-menu-wrap"
                                      ref={
                                        openConnectorRowMenuKey === String(row.id)
                                          ? connectorRowActionsTriggerRef
                                          : null
                                      }
                                    >
                                      <button
                                        type="button"
                                        className={[
                                          'assets-more-btn',
                                          openConnectorRowMenuKey === String(row.id)
                                            ? 'assets-more-btn--open'
                                            : null,
                                          testingById[row.id] ? 'is-busy' : null,
                                        ].filter(Boolean).join(' ')}
                                        title="More actions"
                                        aria-label={`More actions for ${row.name}`}
                                        aria-haspopup="menu"
                                        aria-expanded={openConnectorRowMenuKey === String(row.id)}
                                        disabled={!!testingById[row.id]}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const menuKey = String(row.id);
                                          if (openConnectorRowMenuKey === menuKey) {
                                            closeConnectorRowActionsMenu();
                                            return;
                                          }
                                          const trigger = e.currentTarget;
                                          if (!trigger) return;
                                          const rect = trigger.getBoundingClientRect();
                                          const estimatedMenuHeight = 88;
                                          const openUp =
                                            window.innerHeight - rect.bottom < estimatedMenuHeight + 16;
                                          const isLightTheme = Boolean(
                                            document.querySelector('.dashboard-container.theme-light')
                                          );
                                          setConnectorRowMenuPlacement({
                                            top: openUp ? rect.top - 4 : rect.bottom + 4,
                                            right: Math.max(8, window.innerWidth - rect.right),
                                            openUp,
                                            isLightTheme,
                                          });
                                          setOpenConnectorRowMenuKey(menuKey);
                                        }}
                                      >
                                        {testingById[row.id] ? (
                                          <span className="connectors-settings-action-spinner" aria-hidden="true" />
                                        ) : (
                                          <svg
                                            className="assets-more-btn-icon"
                                            viewBox="0 0 20 20"
                                            width="14"
                                            height="14"
                                            fill="currentColor"
                                            aria-hidden="true"
                                          >
                                            <circle cx="4" cy="10" r="1.6" />
                                            <circle cx="10" cy="10" r="1.6" />
                                            <circle cx="16" cy="10" r="1.6" />
                                          </svg>
                                        )}
                                      </button>
                                      {openConnectorRowMenuKey === String(row.id) && connectorRowMenuPlacement
                                        ? createPortal(
                                            <div
                                              ref={connectorRowActionsMenuRef}
                                              className={[
                                                'assets-row-actions-menu',
                                                'assets-row-actions-menu--portal',
                                                connectorRowMenuPlacement.openUp
                                                  ? 'assets-row-actions-menu--up'
                                                  : null,
                                                connectorRowMenuPlacement.isLightTheme
                                                  ? 'assets-row-actions-menu--light'
                                                  : null,
                                              ]
                                                .filter(Boolean)
                                                .join(' ')}
                                              role="menu"
                                              style={{
                                                top: connectorRowMenuPlacement.top,
                                                right: connectorRowMenuPlacement.right,
                                              }}
                                            >
                                              <button
                                                type="button"
                                                role="menuitem"
                                                className="assets-row-actions-menu-item"
                                                disabled={!!testingById[row.id] || !onTestConnector}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  closeConnectorRowActionsMenu();
                                                  runConnectorTest(row);
                                                }}
                                              >
                                                Test
                                              </button>
                                              <button
                                                type="button"
                                                role="menuitem"
                                                className="assets-row-actions-menu-item"
                                                data-tour="settings-connectors-configure"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  closeConnectorRowActionsMenu();
                                                  onConfigureConnector
                                                    && onConfigureConnector(row.id, connectorsScannerTab);
                                                }}
                                              >
                                                Configure
                                              </button>
                                            </div>,
                                            document.body
                                          )
                                        : null}
                                    </div>
                                  </>
                                ) : (
                                  <span className="connectors-settings-status connectors-settings-status--neutral">Read-only</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
          <TablePagination {...tablePagination} itemLabel="connectors" />
        </div>
      </div>
    </section>
  );
}

export default ConnectorsSettingsPage;
